import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { Property } from '../lib/types';

export function useProperties() {
  return useQuery<Property[]>({
    queryKey: ['properties'],
    queryFn: async () => (await api.get('/crm/properties')).data.items,
  });
}

export function useProperty(id: string | null) {
  return useQuery<Property>({
    queryKey: ['properties', id],
    queryFn: async () => (await api.get(`/crm/properties/${id}`)).data,
    enabled: Boolean(id),
    refetchInterval: (q) =>
      q.state.data?.media?.some((m) => m.status === 'uploading') ? 3000 : false,
  });
}

export function useSaveProperty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: Partial<Property> & { property_id?: string }) => {
      const { property_id, ...body } = p;
      const res = property_id
        ? await api.put(`/crm/properties/${property_id}`, body)
        : await api.post('/crm/properties', body);
      return res.data as Property;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['properties'] }),
  });
}

export function useUploadPhotos(propertyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (files: File[]) => {
      for (const file of files) {
        const { data } = await api.post(`/crm/properties/${propertyId}/media`, {
          content_type: file.type,
        });
        await fetch(data.upload_url, {
          method: 'PUT',
          headers: { 'Content-Type': file.type },
          body: file,
        });
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['properties', propertyId] }),
  });
}

// ── Altas que llegan del portal del cliente ───────────────────────
//
// Un propietario sube su vivienda desde /cuenta y entra aquí como
// `pendiente_validacion`, sin código HAB-XXX y sin publicar. Estas mutaciones
// son las que la meten en la cartera o la devuelven con un motivo.

export function useAprobarAlta() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      propertyId,
      publicar,
    }: {
      propertyId: string;
      publicar: boolean;
    }) =>
      (await api.post(`/crm/properties/${propertyId}/aprobar`, {
        publicada_web: publicar,
      })).data as Property,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['properties'] }),
  });
}

export function useRechazarAlta() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ propertyId, motivo }: { propertyId: string; motivo: string }) =>
      (await api.post(`/crm/properties/${propertyId}/rechazar`, { motivo })).data as Property,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['properties'] }),
  });
}

/** Propuesta de cambio sobre una vivienda ya publicada: se aplica o se descarta. */
export function useResolverCambios() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      propertyId,
      aceptar,
    }: {
      propertyId: string;
      aceptar: boolean;
    }) =>
      (await api.post(
        `/crm/properties/${propertyId}/cambios/${aceptar ? 'aprobar' : 'rechazar'}`,
        {},
      )).data as Property,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['properties'] }),
  });
}

export function useDeletePhoto(propertyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (mediaId: string) =>
      api.delete(`/crm/properties/${propertyId}/media/${mediaId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['properties', propertyId] }),
  });
}
